import React, {
  Children,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * اسلایدر شناور و شیشه‌ای (Glassy Carousel)
 *
 * - آیتم وسط بزرگ‌تر و واضح‌تر است، آیتم‌های کناری کمی کوچک، محو و کج می‌شوند.
 * - با موس درگ می‌شود، با لمس روان اسکرول می‌کند، فلش و نقطه دارد.
 * - کاملاً مستقل از داده است؛ هر کارتی را می‌شود داخلش گذاشت.
 */
export default function GlassCarousel({
  children,
  className,
  itemClassName,
  autoPlay = 0,
  showArrows = true,
  showDots = true,
}) {
  const items = Children.toArray(children);
  const trackRef = useRef(null);
  const frame = useRef(0);
  const drag = useRef({ active: false, moved: false, startX: 0, startScroll: 0 });
  const hovering = useRef(false);

  const [active, setActive] = useState(0);
  const [edge, setEdge] = useState({ start: true, end: false });

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const box = track.getBoundingClientRect();
    const center = box.left + box.width / 2;
    let best = 0;
    let bestDist = Infinity;

    Array.from(track.children).forEach((node, index) => {
      const rect = node.getBoundingClientRect();
      const nodeCenter = rect.left + rect.width / 2;
      const raw = (nodeCenter - center) / (rect.width || 1);
      const d = Math.max(-2, Math.min(2, raw));
      const abs = Math.abs(d);

      node.style.setProperty("--gc-s", String(1 - Math.min(abs, 1.6) * 0.075));
      node.style.setProperty("--gc-r", `${(-d * 7).toFixed(2)}deg`);
      node.style.setProperty("--gc-o", String(1 - Math.min(abs, 1.6) * 0.28));
      node.classList.toggle("is-center", abs < 0.5);

      if (Math.abs(raw) < bestDist) {
        bestDist = Math.abs(raw);
        best = index;
      }
    });

    setActive(best);

    const max = track.scrollWidth - track.clientWidth;
    const pos = Math.abs(track.scrollLeft);
    setEdge({ start: pos < 8, end: pos > max - 8 });
  }, []);

  const schedule = useCallback(() => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(update);
  }, [update]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    update();
    track.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    return () => {
      cancelAnimationFrame(frame.current);
      track.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [schedule, update, items.length]);

  const goTo = useCallback((index) => {
    const track = trackRef.current;
    const node = track?.children[index];
    if (!node) return;
    node.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, []);

  const step = useCallback(
    (direction) => {
      const next = Math.max(0, Math.min(items.length - 1, active + direction));
      goTo(next);
    },
    [active, goTo, items.length]
  );

  /* پخش خودکار (اختیاری) */
  useEffect(() => {
    if (!autoPlay || items.length < 2) return undefined;

    const id = setInterval(() => {
      if (hovering.current || drag.current.active) return;
      goTo(active >= items.length - 1 ? 0 : active + 1);
    }, autoPlay);

    return () => clearInterval(id);
  }, [autoPlay, active, goTo, items.length]);

  /* درگ با موس */
  const onPointerDown = (event) => {
    if (event.pointerType !== "mouse") return;
    const track = trackRef.current;
    drag.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startScroll: track.scrollLeft,
    };
  };

  const onPointerMove = (event) => {
    const state = drag.current;
    if (!state.active) return;
    const dx = event.clientX - state.startX;
    if (Math.abs(dx) > 6) {
      state.moved = true;
      trackRef.current.classList.add("is-dragging");
    }
    if (state.moved) trackRef.current.scrollLeft = state.startScroll - dx;
  };

  const endDrag = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    trackRef.current?.classList.remove("is-dragging");
    // کمی تاخیر تا کلیکِ بعد از درگ روی لینک‌ها اجرا نشود
    setTimeout(() => {
      drag.current.moved = false;
    }, 30);
  };

  const onClickCapture = (event) => {
    if (drag.current.moved) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  if (items.length === 0) return null;

  return (
    <div
      className={cn("gc-wrap", className)}
      onMouseEnter={() => (hovering.current = true)}
      onMouseLeave={() => (hovering.current = false)}
    >
      <div
        ref={trackRef}
        className="gc-track"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
      >
        {items.map((child, index) => (
          <div key={child.key ?? index} className={cn("gc-item", itemClassName)}>
            {child}
          </div>
        ))}
      </div>

      {showArrows && items.length > 1 && (
        <>
          <button
            type="button"
            aria-label="قبلی"
            className="gc-arrow right-1 sm:-right-3"
            disabled={edge.start}
            onClick={() => step(-1)}
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <button
            type="button"
            aria-label="بعدی"
            className="gc-arrow left-1 sm:-left-3"
            disabled={edge.end}
            onClick={() => step(1)}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </>
      )}

      {showDots && items.length > 1 && (
        <div className="mt-2 flex items-center justify-center gap-2">
          {items.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`مورد ${index + 1}`}
              onClick={() => goTo(index)}
              className={cn("gc-dot", index === active && "is-active")}
            />
          ))}
        </div>
      )}
    </div>
  );
}
